import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { USER_ROLE_LABELS } from '@faktorplus/shared';
import { useAuth, useCurrentUser } from '../../auth/auth-provider';
import { useDashboardSummary } from '../../api/reports';
import { useInvoiceList } from '../../api/invoices';
import { ThemeToggle } from '../../theme/theme-toggle';
import { navigationFor, type NavItem } from './navigation';
import logoUrl from '../../assets/logo.png';
import styles from './app-shell.module.css';

export function AppShell() {
  const user = useCurrentUser();
  const counters = useNavCounters();

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <Link to="/" className={styles.brand} aria-label="Faktor Plus — pulpit">
            <img src={logoUrl} alt="" className={styles.brandLogo} />
            <span className={styles.brandName}>
              Faktor<em>Plus</em>
            </span>
          </Link>

          <nav className={styles.nav} aria-label="Nawigacja główna">
            {navigationFor(user.role).map((item) => (
              <NavItemLink key={item.to} item={item} count={counters[item.badge ?? 'none']} />
            ))}
          </nav>

          <div className={styles.right}>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

function NavItemLink({ item, count }: { item: NavItem; count: number }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
      }
    >
      {item.shortLabel ? (
        <>
          <span className={styles.labelWide}>{item.label}</span>
          <span className={styles.labelNarrow}>{item.shortLabel}</span>
        </>
      ) : (
        item.label
      )}
      {count > 0 && <span className={styles.badge}>{count}</span>}
    </NavLink>
  );
}

/**
 * The signed-in identity, collapsed to just the avatar until asked for.
 *
 * On the topbar only the initials show; name, role and the way out live behind
 * one click. That keeps the bar's right side to ~30px, which is what lets the
 * navigation breathe on a tablet instead of scrolling off behind an edge.
 */
function UserMenu() {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a menu you cannot dismiss the same
  // way you open everything else reads as a bug, not a feature.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.userMenu} ref={ref}>
      <button
        type="button"
        className={styles.avatarButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${user.fullName} · ${USER_ROLE_LABELS[user.role]}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials(user.fullName)}
        </span>
        <svg className={styles.chevron} viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className={styles.menuPanel} role="menu">
          <div className={styles.menuIdentity}>
            <span className={styles.menuName}>{user.fullName}</span>
            <span className={styles.menuRole}>{USER_ROLE_LABELS[user.role]}</span>
          </div>
          <div className={styles.menuDivider} />
          <div className={styles.menuRow}>
            <span className={styles.menuRowLabel}>Ciemny motyw</span>
            <ThemeToggle />
          </div>
          <div className={styles.menuDivider} />
          <button
            type="button"
            role="menuitem"
            className={styles.menuLogout}
            onClick={() => void logout()}
          >
            Wyloguj
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Badge counts for the navigation.
 *
 * "Waiting for me" means different states for different people: a site manager
 * is blocked on ASSIGNED invoices, the owner on ones the manager already
 * cleared. The dashboard endpoint applies the caller's own visibility filter,
 * so both numbers come from it rather than from a second source of truth.
 */
function useNavCounters(): Record<string, number> {
  const user = useCurrentUser();
  const { data: summary } = useDashboardSummary();

  // Only the accountant has a "do przypisania" badge; the other roles were
  // paying for this request on every navigation and discarding the answer.
  const { data: unassigned } = useInvoiceList(
    { status: 'NEW', pageSize: 1 },
    { enabled: user.role === 'ACCOUNTANT' },
  );

  const awaitingMe =
    user.role === 'SITE_MANAGER'
      ? (summary?.awaitingManagers.count ?? 0)
      : (summary?.awaitingMyApproval.count ?? 0);

  return {
    none: 0,
    awaitingMe,
    toAssign: unassigned?.total ?? 0,
  };
}

function initials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
